/**
 * Oynatma denetimi — `<video>` öğesini tek yerden dinler.
 *
 * ## Neden kendi denetim çubuğumuz?
 *
 * Kademeli (progressive) HLS'te hls.js süreyi **şu an kodlanmış segmentlerin
 * toplamından** hesaplar. Gerçek sunucuda 51 dakikalık film oynatıcıda
 * "18:49" gösteriyordu: kullanıcı filmin bittiğini sanabilir, geriye ne kaldı
 * anlamaz.
 *
 * Doğru toplam süre zaten elimizde: `item.durationMs` (katalogdan, 51 dk).
 * Tarayıcının kendi çubuğu bu değeri kullanamaz — `video.duration` hls.js
 * tarafından belirlenir. Bu yüzden çubuğu biz kuruyoruz.
 *
 * ## "Hazır" nedir?
 *
 * `video.seekable` tarayıcının **gerçekten oynatabildiği** aralıktır; HLS
 * için bu, kodlanmış (ve sunucudan istenebilir) kısımdır. `duration` değil —
 * `seekable.end()` kullanılır, çünkü kalan süre kodlansa bile istemci henüz
 * segment bilmiyor olabilir.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadPlayerPrefs, savePlayerPrefs } from './playerPrefs.ts';
import type { ElementRef } from './useElementRef.ts';

export interface PlayerControls {
  isPlaying: boolean;
  isEnded: boolean;
  /** Oynatıcının oynatabildiği aralığın sonu (ms). Kodlanan kısım. */
  availableMs: number;
  /** Tamponlanmış aralığın sonu (ms). */
  bufferedMs: number;
  /** Oynatma konumu (ms). `timeupdate` (~4 Hz) ile tazelenir. */
  positionMs: number;
  /** Gösterilecek toplam süre (ms): katalog, yoksa oynatıcı, yoksa 0. */
  totalMs: number;
  volume: number;
  muted: boolean;
  playbackRate: number;
  /** Kullanıcı hazır olmayan bir konuma atlamayı denedi. */
  blockedSeek: boolean;
  togglePlay: () => void;
  seekToMs: (ms: number) => void;
  seekRelative: (deltaMs: number) => void;
  setVolume: (value: number) => void;
  toggleMute: () => void;
  setPlaybackRate: (value: number) => void;
}

export interface UsePlayerControlsOptions {
  /** Katalogdaki gerçek toplam süre (ms). */
  totalMs: number;
  /**
   * Akışın en erken oynatılabilir konumu (ms).
   *
   * Konumdan başlayan dönüştürmede (kalite düşürmesi) segmentler ör. 600.
   * saniyeden başlar; 600'ün altına atlamak mevcut akışla mümkün değildir.
   */
  seekFloorMs?: number | undefined;
  /**
   * `seekFloorMs` altına atlama istendiğinde çağrılır.
   *
   * Çağıran taraf akışı istenen konumdan **yeniden hazırlar**; yoksa sarma
   * sessizce akışın başına çekilir ve kullanıcı neden geri gidemediğini
   * anlamaz.
   */
  onSeekBeforeStart?: ((ms: number) => void) | undefined;
  /** Konum değiştiğinde bildir (izleme raporu için). */
  onPosition?: (ms: number) => void;
  /** Tam ekran aç/kapat geri çağrısı (F tuşu için). */
  onToggleFullscreen?: (() => void) | undefined;
  /** Kısayollar kılavuzu aç/kapat geri çağrısı (? tuşu için). */
  onToggleHelp?: (() => void) | undefined;
  /** Altyazı aç/kapat geri çağrısı (C tuşu için). */
  onToggleSubtitles?: (() => void) | undefined;
  /** Sıradaki içeriğe geç (N tuşu için). */
  onNext?: (() => void) | undefined;
}

/** Akışın başlangıcından bu kadar önceki atlamalar "geri" sayılır. */
const SEEK_RESTART_TOLERANCE_MS = 500;

export interface SeekTarget {
  /** Oynatıcıya uygulanacak konum (ms). */
  target: number;
  /** İstenen konum akışın başlangıcından önce: akış yeniden hazırlanmalı. */
  requiresRestart: boolean;
}

/**
 * Sarma isteğini güvenli bir konuma indirger.
 *
 * Saf fonksiyon: `seekToMs` içindeki sınırlama böylece test edilebilir.
 *
 * - İstenen konum **akışın başlangıcından önceyse** `requiresRestart` döner;
 *   çağıran akışı o konumdan yeniden başlatır (aksi hâlde oynatıcı sessizce
 *   `seekable.start`e çekilir).
 * - Aksi hâlde mevcut davranış korunur: hazır olmayan bölgeye atlamak yerine
 *   `seekable.end`/toplam süre sınırına çekilir.
 */
export function resolveSeekTarget(
  requestedMs: number,
  options: { totalMs: number; seekableEndMs: number; floorMs?: number | undefined },
): SeekTarget {
  const floor = Math.max(0, options.floorMs ?? 0);
  const requested = Number.isFinite(requestedMs) ? requestedMs : 0;

  if (floor > 0 && requested < floor - SEEK_RESTART_TOLERANCE_MS) {
    return { target: floor, requiresRestart: true };
  }

  const seekableEnd = options.seekableEndMs;
  const fallbackLimit = options.totalMs;
  const limit = seekableEnd > 0 ? seekableEnd : fallbackLimit;
  const maxLimit = Math.max(0, limit);
  const upper = Math.max(0, options.totalMs > 0 ? Math.min(options.totalMs, maxLimit) : maxLimit);

  return { target: clamp(requested, floor, upper), requiresRestart: false };
}

/**
 * Oynatıcı denetimleri.
 *
 * @param videoRef **Geç bağlanan** öğe referansı (`<video ref={videoRef.attach}>`).
 *
 * Neden geç bağlanan: oynatma hazır değilken `WatchScreen` `<Preparing>`
 * döndürür, `<video>` DOM'da **yoktur**. Klasik `useEffect([ref], ...)` deseni
 * burada boş kalırdı: kanca `ref.current === null` görüp erken döner,
 * bağımlılık **hiç değişmez** ve hiçbir dinleyici bağlanmaz.
 *
 * Gerçek sunucuda tam olarak bu oldu: film oynuyordu ama süre 0:00'da donuyor,
 * oynat ikonu hiç değişmiyordu — kod doğruydu, sadece çağrılmadı. Aynı hata
 * sınıfı üç kez oldu; bkz. `useElementRef`.
 */
export function usePlayerControls(
  videoRef: ElementRef<HTMLVideoElement>,
  options: UsePlayerControlsOptions,
): PlayerControls {
  const {
    totalMs,
    onPosition,
    onToggleFullscreen,
    onToggleHelp,
    onToggleSubtitles,
    onNext,
    seekFloorMs = 0,
    onSeekBeforeStart,
  } = options;
  const onToggleFullscreenRef = useRef(onToggleFullscreen);
  useEffect(() => {
    onToggleFullscreenRef.current = onToggleFullscreen;
  }, [onToggleFullscreen]);

  const onToggleHelpRef = useRef(onToggleHelp);
  useEffect(() => {
    onToggleHelpRef.current = onToggleHelp;
  }, [onToggleHelp]);

  const onToggleSubtitlesRef = useRef(onToggleSubtitles);
  useEffect(() => {
    onToggleSubtitlesRef.current = onToggleSubtitles;
  }, [onToggleSubtitles]);

  const onNextRef = useRef(onNext);
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [availableMs, setAvailableMs] = useState(0);
  const [bufferedMs, setBufferedMs] = useState(0);
  const [positionMs, setPositionMs] = useState(0);
  // Katalogda süre yoksa oynatıcının bildirdiği değer kullanılır.
  const [playerDurationMs, setPlayerDurationMs] = useState(0);
  const [initialPrefs] = useState(loadPlayerPrefs);
  const [volume, setVolumeState] = useState(initialPrefs.volume);
  const [muted, setMuted] = useState(initialPrefs.muted);
  const [playbackRate, setRateState] = useState(initialPrefs.rate);
  const [blockedSeek, setBlockedSeek] = useState(false);
  // `onPosition` her render'da yeni bir fonksiyon olabilir; depoya yazıp
  // etiket içinde okumak, olay dinleyicisini her render'da kurmamızı sağlar.
  const onPositionRef = useRef(onPosition);
  useEffect(() => {
    onPositionRef.current = onPosition;
  }, [onPosition]);

  const onSeekBeforeStartRef = useRef(onSeekBeforeStart);
  useEffect(() => {
    onSeekBeforeStartRef.current = onSeekBeforeStart;
  }, [onSeekBeforeStart]);

  useEffect(() => {
    // Bağımlılık **öğenin kendisi**: `<video>` DOM'a girdiğinde etiket yeniden
    // kurulur. `videoRef.ref` (sabit nesne) bağımlılık olsaydı hiç çalışmazdı.
    const video = videoRef.element;
    if (video === null) return;

    /*
     * Kayıtlı oynatıcı tercihlerini **yeni medya öğesine** uygula.
     *
     * Parça/video değişiminde `WatchScreen` yeniden kurulur; yeni `<video>`
     * varsayılan `volume=1` ile doğar. Tercih burada uygulanmazsa kullanıcı
     * her parçada sesi yeniden kısmak zorunda kalır (Öneri 4).
     */
    const prefs = loadPlayerPrefs();
    video.volume = prefs.volume;
    video.muted = prefs.muted;
    video.playbackRate = prefs.rate;

    const readRanges = (ranges: TimeRanges, end: (v: number) => void): void => {
      const last = ranges.length - 1;
      end(last < 0 ? 0 : ranges.end(last) * 1000);
    };

    const onTimeUpdate = (): void => {
      const ms = video.currentTime * 1000;
      setPositionMs(ms);
      onPositionRef.current?.(ms);
      // `seekable` her `timeupdate`'ta tazelenmez; yalnızca segment eklendiğinde
      // büyür, o da `progress`/`durationchange` ile bildirilir.
      // React aynı değerde yeniden render etmez; bildirim bir sonraki karede
      // kendiliğinden söner.
      setBlockedSeek(false);
    };

    const onProgress = (): void => {
      readRanges(video.buffered, setBufferedMs);
      readRanges(video.seekable, setAvailableMs);
    };

    /**
     * Yedek toplam süre.
     *
     * Katalogda `durationMs` **null** olabilir (probe başarısız). O zaman
     * oynatıcının bildirdiği süre kullanılır — kademeli HLS'te bu eksik olabilir
     * ama sıfırdan iyidir.
     */
    const onDuration = (): void => {
      setPlayerDurationMs(
        video.duration > 0 && Number.isFinite(video.duration) ? video.duration * 1000 : 0,
      );
    };

    const onSeek = (): void => {
      onProgress();
      setPositionMs(video.currentTime * 1000);
    };

    // Olay dinleyicileri açık listeyle bağlanır: `Record` + `!` yerine temizlik
    // fonksiyonları toplanır, hem tip güvenli hem de söküm sırası belli olur.
    const cleanups: Array<() => void> = [];
    const on = <K extends keyof HTMLMediaElementEventMap>(
      type: K,
      listener: (event: HTMLMediaElementEventMap[K]) => void,
    ): void => {
      const handler = listener as EventListener;
      video.addEventListener(type, handler);
      cleanups.push(() => video.removeEventListener(type, handler));
    };

    on('timeupdate', onTimeUpdate);
    /*
     * `play` yalnızca "oynuyor" bilgisini değiştirmez; **önceki "bitti"
     * durumunu da geçersiz kılar.**
     *
     * Kullanıcı biten bir parçayı başa sarıp yeniden oynatırsa `isEnded`
     * `true` kalırsa `useAutoAdvance` aynı bitiş için ikinci kez geçiş
     * yapmaz ve otomatik geçiş bir daha çalışmaz. Ayrıca bileşen yeniden
     * kullanılırsa eski "bitti" durumu yeni parçaya taşınır.
     */
    on('play', () => {
      setIsPlaying(true);
      setIsEnded(false);
    });
    on('pause', () => setIsPlaying(false));
    on('ended', () => {
      setIsPlaying(false);
      setIsEnded(true);
    });
    on('progress', onProgress);
    on('loadedmetadata', () => {
      onProgress();
      // Yeni kaynak yüklendi: önceki kaynağın bitiş bilgisi geçersiz.
      setIsEnded(false);
    });
    on('durationchange', onDuration);
    on('seeked', onSeek);
    on('seeking', onSeek);
    on('volumechange', () => {
      setVolumeState(video.volume);
      setMuted(video.muted);
      savePlayerPrefs({ volume: video.volume, muted: video.muted, rate: video.playbackRate });
    });
    on('ratechange', () => {
      setRateState(video.playbackRate);
      savePlayerPrefs({ volume: video.volume, muted: video.muted, rate: video.playbackRate });
    });
    // (video olay dinleyicileri)

    // Oynatıcı hazır olduğunda ilk değerler alınsın (kaynak sonradan bağlanıyor).
    onProgress();
    onDuration();
    setVolumeState(video.volume);
    setMuted(video.muted);
    setRateState(video.playbackRate);

    return () => {
      for (const off of cleanups) off();
    };
    // Öğe değişirse (koşullu render) dinleyiciler sökülüp yeniden bağlanır.
  }, [videoRef.element]);

  const togglePlay = useCallback(() => {
    const video = videoRef.ref.current;
    if (video === null) return;
    if (video.paused) {
      console.log('[usePlayerControls togglePlay -> play()]');
      void video.play().catch(() => undefined);
    } else {
      console.log('[usePlayerControls togglePlay -> pause()]');
      video.pause();
    }
  }, [videoRef.ref]);

  // Katalogda süre yoksa oynatıcının bildirdiği değere düşülür.
  const effectiveTotal = totalMs > 0 ? totalMs : playerDurationMs;

  const seekToMs = useCallback(
    (ms: number) => {
      const video = videoRef.ref.current;
      if (video === null) return;

      // Hazır olmayan bölgeye atlamak anlamsızdır: segment henüz yok, oynatıcı
      // boşa bekler ve kullanıcı neden durduğunu anlamaz. Sınırı aşan istek
      // kenara çekilir ve kullanıcıya bildirilir.
      const last = video.seekable ? video.seekable.length - 1 : -1;
      const seekableEnd = last >= 0 ? video.seekable.end(last) * 1000 : 0;
      const fallbackLimit = video.duration ? video.duration * 1000 : effectiveTotal;
      const limit = seekableEnd > 0 ? seekableEnd : fallbackLimit;
      const resolved = resolveSeekTarget(ms, {
        totalMs: effectiveTotal,
        seekableEndMs: limit,
        floorMs: seekFloorMs,
      });

      /*
       * Akışın başlangıcından öncesi isteniyorsa oynatıcıyı zorlamak yerine
       * akışı o konumdan yeniden hazırla. Aksi hâlde tarayıcı isteği sessizce
       * `seekable.start`e çeker ve kullanıcı "geri sarma çalışmıyor" görür.
       */
      if (resolved.requiresRestart) {
        const requested = Number.isFinite(ms) ? Math.max(0, Math.round(ms)) : 0;
        onSeekBeforeStartRef.current?.(requested);
        return;
      }

      if (limit > 0 && ms > limit + 1) setBlockedSeek(true);

      video.currentTime = resolved.target / 1000;
    },
    [effectiveTotal, seekFloorMs, videoRef.ref],
  );

  const seekRelative = useCallback(
    (deltaMs: number) => {
      const video = videoRef.ref.current;
      if (video === null) return;
      seekToMs(video.currentTime * 1000 + deltaMs);
    },
    [seekToMs, videoRef.ref],
  );

  // Klavye kısayolları: pencere geneline bağlanır; odak `<video>` dışında
  // olsa da (ör. denetim çubuğu tıklandıktan sonra) ok tuşları ve boşluk çalışır.
  useEffect(() => {
    const video = videoRef.element;
    if (video === null) return;

    const onWindowKeyDown = (event: KeyboardEvent): void => {
      // Modal/dialog açıkken oynatıcı kısayolları arka planda çalışmamalı.
      if (
        document.querySelector(
          '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]',
        ) !== null
      ) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      onKeyDown(
        event,
        video,
        seekToMs,
        () => onToggleFullscreenRef.current?.(),
        () => onToggleHelpRef.current?.(),
        () => onToggleSubtitlesRef.current?.(),
        () => onNextRef.current?.(),
      );
    };

    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, [videoRef.element, seekToMs]);

  const setVolume = useCallback(
    (value: number) => {
      const video = videoRef.ref.current;
      if (video === null) return;
      video.volume = clamp(value, 0, 1);
      // Sesi açmak sessiz oynatmaya yol açmasın.
      if (video.volume > 0) video.muted = false;
    },
    [videoRef.ref],
  );

  const toggleMute = useCallback(() => {
    const video = videoRef.ref.current;
    if (video === null) return;
    video.muted = !video.muted;
  }, [videoRef.ref]);

  const setPlaybackRate = useCallback(
    (value: number) => {
      const video = videoRef.ref.current;
      if (video === null) return;
      video.playbackRate = value;
    },
    [videoRef.ref],
  );

  return {
    isPlaying,
    isEnded,
    availableMs,
    bufferedMs,
    positionMs,
    totalMs: effectiveTotal,
    volume,
    muted,
    playbackRate,
    blockedSeek,
    togglePlay,
    seekToMs,
    seekRelative,
    setVolume,
    toggleMute,
    setPlaybackRate,
  };
}

/** Atlanacak süre (ms). */
const SEEK_STEP_MS = 10_000;

/**
 * Klavye kısayolları.
 *
 * `window` geneline dinleyici bağlanır, böylece odak doğrudan `<video>` üzerinde
 * olmasa da ok tuşları, J/L (10 sn), Space/K (oynat/duraklat), M (sessiz), F (tam ekran),
 * C (altyazı aç/kapat), N (sıradaki içerik) ve ? (kısayollar kılavuzu) çalışır.
 */
export function onKeyDown(
  event: KeyboardEvent,
  video: HTMLVideoElement,
  seekToMs: (ms: number) => void,
  onToggleFullscreen?: (() => void) | undefined,
  onToggleHelp?: (() => void) | undefined,
  onToggleSubtitles?: (() => void) | undefined,
  onNext?: (() => void) | undefined,
): void {
  switch (event.key) {
    case ' ':
    case 'k':
    case 'K':
      event.preventDefault();
      if (video.paused) {
        void video.play().catch(() => undefined);
      } else {
        video.pause();
      }
      return;
    case 'ArrowLeft':
    case 'j':
    case 'J': {
      event.preventDefault();
      seekToMs(video.currentTime * 1000 - SEEK_STEP_MS);
      return;
    }
    case 'ArrowRight':
    case 'l':
    case 'L': {
      event.preventDefault();
      seekToMs(video.currentTime * 1000 + SEEK_STEP_MS);
      return;
    }
    case 'ArrowUp':
    case 'ArrowDown': {
      event.preventDefault();
      const delta = event.key === 'ArrowUp' ? 0.05 : -0.05;
      video.volume = clamp(video.volume + delta, 0, 1);
      if (video.volume > 0) video.muted = false;
      return;
    }
    case 'm':
    case 'M':
      event.preventDefault();
      video.muted = !video.muted;
      return;
    case 'f':
    case 'F':
      event.preventDefault();
      onToggleFullscreen?.();
      return;
    case '?':
      event.preventDefault();
      onToggleHelp?.();
      return;
    case 'c':
    case 'C':
      event.preventDefault();
      onToggleSubtitles?.();
      return;
    case 'n':
    case 'N':
      event.preventDefault();
      onNext?.();
      return;
    default:
  }
}

/** `value`'yi `[min, max]` aralığına sınırlar. */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Çubuk dolgu yüzdesi.
 *
 * Sıfır süre koruması **şart**: katalogda `durationMs` null ise toplam 0 gelir
 * ve `ms / 0` → `NaN` → `width: NaN%` geçersiz olur. CSS bunu sessizce
 * yutar, çubuk görünmez olur ve "ilerleme çubuğu bozuk" sanılır.
 *
 * Çubuğun `max` değeri **gerçek süredir**, böylece eksik (kodlanmamış) bölge
 * de görünür.
 */
export function barPercent(ms: number, totalMs: number): number {
  const total = totalMs > 0 ? totalMs : 1;
  const value = (ms / total) * 100;
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
}
